import { useEffect, useState } from "react";
import { CheckCircle2, MailCheck, Loader2, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";
import { formatDateTimeAU } from "@/lib/formatDateAU";
import { toast } from "sonner";

/**
 * Admin-only Estimate Request status card.
 *
 * Renders inside the published-test column of LabDetail. Shows:
 *   • Whether the customer has clicked the signed link in the
 *     Pristine Plain-English Summary PDF
 *   • Requested date/time, proposal id, status
 *   • A "Send estimate request now" button so admins can manually
 *     trigger the same workflow (idempotent — does not duplicate)
 *
 * Phase 2J-AA originally introduced this card as a "Proposal Request".
 * Phase 2J-AB renamed the customer-facing wording to "Estimate" —
 * the underlying backend model is unchanged (still `proposal_request`)
 * to avoid migration risk.
 */
export function ProposalRequestStatusCard({ testId }) {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  async function load() {
    setLoading(true); setError(null);
    try {
      const { data } = await api.get(`/admin/tests/${testId}/proposal-request`);
      setData(data);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { if (testId) load(); /* eslint-disable-line */ }, [testId]);

  async function handleResend() {
    if (!confirm(
      "This will create the estimate request now (if one doesn't already exist) "
      + "and send the customer + PWS emails. Continue?"
    )) return;
    setBusy(true);
    try {
      const { data: r } = await api.post(`/admin/tests/${testId}/proposal-request/resend`);
      if (r.already_requested) {
        toast.info("Estimate request already exists for this test — no new emails sent.");
      } else {
        toast.success("Estimate request created and emails sent.");
      }
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <section
        data-testid="proposal-request-status-card"
        className="rounded-3xl bg-white border border-slate-200 p-6"
      >
        <div className="flex items-center gap-2 text-slate-500 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading estimate request status…
        </div>
      </section>
    );
  }
  if (error) {
    return (
      <section
        data-testid="proposal-request-status-card"
        className="rounded-3xl bg-white border border-rose-200 p-6 text-sm text-rose-700"
      >
        Could not load estimate request status: {error}
      </section>
    );
  }

  const pr = data?.proposal_request;
  const requested = !!pr?.proposal_id;
  const requestedAt = pr?.requested_at || null;
  // Phase 2J-AF — inline estimate lines summary (admin-only).
  // `matched` = lines that mapped to a catalogue product.
  // `review_required` = lines created because tank type/qty or catalogue
  // mapping needs admin confirmation. Only show when a request exists.
  const linesSummary = data?.lines_summary;

  return (
    <section
      data-testid="proposal-request-status-card"
      className="rounded-3xl bg-white border border-slate-200 p-6"
    >
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <MailCheck className="w-4 h-4 text-slate-600" />
          <h3 className="font-display text-lg font-semibold text-slate-900">
            Estimate request
          </h3>
        </div>
        <span
          data-testid="proposal-request-status-badge"
          className={
            "text-xs font-semibold px-2.5 py-1 rounded-full "
            + (requested
                ? "bg-emerald-100 text-emerald-800"
                : "bg-slate-100 text-slate-600")
          }
        >
          {requested ? "Requested" : "Not requested"}
        </span>
      </div>

      <dl className="space-y-2 text-sm">
        <div className="flex justify-between gap-3">
          <dt className="text-slate-500">Estimate requested</dt>
          <dd
            data-testid="proposal-request-status-yesno"
            className="font-semibold text-slate-900"
          >
            {requested ? "Yes" : "No"}
          </dd>
        </div>
        {requested && (
          <>
            <div className="flex justify-between gap-3">
              <dt className="text-slate-500">Requested at</dt>
              <dd
                data-testid="proposal-request-status-time"
                className="text-slate-700"
              >
                {requestedAt
                  ? formatDateTimeAU(requestedAt)
                  : "—"}
              </dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-slate-500">Status</dt>
              <dd className="text-slate-700">
                {pr?.status || data?.status || "requested"}
              </dd>
            </div>
            {pr?.proposal_id && (
              <div className="flex justify-between gap-3">
                <dt className="text-slate-500">Estimate record</dt>
                <dd>
                  <a
                    data-testid="proposal-request-status-link"
                    href={`/admin/proposals/${pr.proposal_id}`}
                    className="text-cyan-700 underline hover:text-cyan-800"
                  >
                    {pr.proposal_reference || pr.proposal_id.slice(0, 8)}
                  </a>
                </dd>
              </div>
            )}
            {linesSummary && linesSummary.total > 0 && (
              <div className="flex justify-between gap-3">
                <dt className="text-slate-500">Estimate lines</dt>
                <dd
                  data-testid="proposal-request-lines-summary"
                  className="text-slate-700"
                >
                  <span className="font-semibold text-emerald-700">
                    {linesSummary.matched} matched
                  </span>
                  {linesSummary.review_required > 0 && (
                    <>
                      <span className="text-slate-400 mx-1.5">·</span>
                      <span className="font-semibold text-amber-700">
                        {linesSummary.review_required} review-required
                      </span>
                    </>
                  )}
                </dd>
              </div>
            )}
          </>
        )}
      </dl>

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <Button
          data-testid="proposal-request-resend-btn"
          variant={requested ? "outline" : "default"}
          size="sm"
          disabled={busy}
          onClick={handleResend}
        >
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                : (requested ? <CheckCircle2 className="w-4 h-4 mr-2" />
                              : <Send className="w-4 h-4 mr-2" />)}
          {requested
            ? "Resend (idempotent)"
            : "Send estimate request now"}
        </Button>
        {!requested && (
          <span className="text-xs text-slate-500">
            Customer also has a one-click link inside the Pristine Summary PDF.
          </span>
        )}
      </div>
    </section>
  );
}

export default ProposalRequestStatusCard;
